import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ExternalLink, Trash2, Copy, Zap, Archive, RotateCcw, DollarSign, X, Sparkles, ImageIcon } from "lucide-react";
import { fetchListings, fetchOffers, setupForward } from "@/lib/api";
import { api } from "@/lib/api";
import Sidebar from "@/components/dashboard/Sidebar";
import { useBranding } from "@/lib/branding";

// Kept in sync with backend `models.LISTING_CATEGORIES`. Order = display order.
const LISTING_CATEGORIES = [
  "AI", "SaaS", "Finance", "Real Estate", "Crypto",
  "Health", "Commerce", "Media", "Travel", "Other",
];

export default function Listings() {
  const brand = useBranding();
  const [listings, setListings] = useState([]);
  const [offers, setOffers] = useState([]);
  const [tab, setTab] = useState("listings");
  const [editingSlug, setEditingSlug] = useState(null);
  const [priceDraft, setPriceDraft] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [lastClickedIdx, setLastClickedIdx] = useState(null);
  const [bulkPrice, setBulkPrice] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = async () => {
    try {
      const [l, o] = await Promise.all([fetchListings(), fetchOffers()]);
      setListings(l);
      setOffers(o);
    } catch (e) { /* silent */ }
    finally { setListingsLoading(false); }
  };
  useEffect(() => {
    load();
    const iv = setInterval(load, 8000);
    return () => clearInterval(iv);
  }, []);

  // Clear any selected slugs that are no longer in the list (e.g., after delete)
  useEffect(() => {
    setSelected((prev) => {
      const set = new Set(listings.map((l) => l.slug));
      const next = new Set();
      for (const s of prev) if (set.has(s)) next.add(s);
      return next;
    });
  }, [listings]);

  const toggleRow = (slug, idx, ev) => {
    const next = new Set(selected);
    if (ev.shiftKey && lastClickedIdx !== null) {
      // Range select — inclusive between lastClickedIdx and idx
      const [start, end] = idx > lastClickedIdx ? [lastClickedIdx, idx] : [idx, lastClickedIdx];
      const wantSelect = !next.has(slug);
      for (let i = start; i <= end; i++) {
        const s = listings[i]?.slug;
        if (!s) continue;
        if (wantSelect) next.add(s);
        else next.delete(s);
      }
    } else if (ev.ctrlKey || ev.metaKey) {
      // Ctrl/Cmd toggle
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
    } else {
      // Plain click — toggle single
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
    }
    setSelected(next);
    setLastClickedIdx(idx);
  };
  const selectAllVisible = () => {
    const all = new Set(listings.map((l) => l.slug));
    setSelected(all);
  };
  const clearSelection = () => setSelected(new Set());

  const doBulk = async (action, extra = {}) => {
    if (selected.size === 0) {
      toast.error("Select at least one listing");
      return;
    }
    if (action === "delete" && !window.confirm(`Delete ${selected.size} listing(s)? This cannot be undone.`)) return;
    setBulkBusy(true);
    try {
      const r = await api
        .post("/listings/bulk", { slugs: Array.from(selected), action, ...extra })
        .then((res) => res.data);
      const changed = r.modified ?? r.deleted ?? 0;
      toast.success(`Bulk ${action}: ${changed} listing(s) updated`);
      setSelected(new Set());
      setBulkPrice("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk action failed");
    } finally {
      setBulkBusy(false);
    }
  };

  const bulkSetPrice = () => {
    const n = Number(bulkPrice);
    if (!bulkPrice || Number.isNaN(n) || n <= 0) {
      toast.error("Enter a positive price first");
      return;
    }
    doBulk("set_price", { price: n });
  };

  const savePrice = async (slug, value) => {
    const trimmed = String(value ?? "").trim();
    const parsed = trimmed === "" ? null : Number(trimmed);
    if (parsed !== null && (Number.isNaN(parsed) || parsed < 0)) {
      toast.error("Enter a positive number or leave blank");
      return;
    }
    try {
      await api.patch(`/listings/${slug}`, { price: parsed });
      toast.success(parsed === null ? `${slug} → offer-only` : `${slug} → ${parsed}`);
      setEditingSlug(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Price update failed");
    }
  };

  const setStatus = async (slug, status) => {
    try {
      await api.patch(`/listings/${slug}`, { status });
      toast.success(`Marked ${slug} as ${status}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };
  const remove = async (slug) => {
    if (!window.confirm(`Remove listing ${slug}?`)) return;
    try {
      await api.delete(`/listings/${slug}`);
      toast.warning("Listing removed");
      load();
    } catch (e) { /* silent */ }
  };
  const copy = (slug) => {
    const url = `${window.location.origin}/l/${slug}`;
    navigator.clipboard.writeText(url);
    toast.success("Lander URL copied");
  };
  const runForward = async (slug) => {
    try {
      const r = await setupForward(slug);
      if (r.ok) toast.success(r.message);
      else toast.error(r.message);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Setup failed");
    }
  };
  const [aiBusy, setAiBusy] = useState(null); // slug currently generating
  const [aiPreview, setAiPreview] = useState(null); // { slug, headline, ... }
  const [categorizing, setCategorizing] = useState(false);
  const [bulkAi, setBulkAi] = useState({ status: "idle", total: 0, done: 0, failed: 0 });
  const [listingsLoading, setListingsLoading] = useState(true);
  // Category → { hero_url, focal_x, focal_y }. Loaded on-demand when the "Categories" tab opens.
  const [categoryHeroes, setCategoryHeroes] = useState({});
  const loadCategoryHeroes = async () => {
    try {
      const { data } = await api.get("/listings/categories");
      const map = {};
      for (const c of data?.categories || []) {
        map[c.key] = {
          hero_url: c.hero_url || "",
          focal_x: typeof c.focal_x === "number" ? c.focal_x : 50,
          focal_y: typeof c.focal_y === "number" ? c.focal_y : 50,
        };
      }
      setCategoryHeroes(map);
    } catch (e) { /* silent */ }
  };
  useEffect(() => {
    if (tab === "categories") loadCategoryHeroes();
  }, [tab]);
  // On first mount, hydrate bulk state so an in-flight job resumes polling.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/listings/ai-copy/bulk/status");
        if (data && data.status) setBulkAi(data);
      } catch { /* silent */ }
    })();
  }, []);
  const runAiCopy = async (slug) => {
    setAiBusy(slug);
    try {
      const { data } = await api.post(`/listings/${slug}/ai-copy`);
      setAiPreview({ slug, ...data });
      toast.success(`AI copy generated for ${slug}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI copy failed");
    } finally {
      setAiBusy(null);
    }
  };

  /** Inline prompt for setting per-listing logo URL + sold narrative.
   *  Both are optional strings on the listing document — logo shows in
   *  the lander "Why this domain" box, narrative on /sold/{slug}. */
  const editExtras = async (l) => {
    const nextLogo = window.prompt(
      `Domain logo URL for ${l.domain}\n(Blank clears it — leave the default to keep it)`,
      l.domain_logo_url || "",
    );
    if (nextLogo === null) return; // Cancel
    const nextNarrative = window.prompt(
      `Sold case-study narrative (Markdown) for ${l.domain}\n(Only shown on /sold/${l.slug}; blank uses the default template)`,
      l.sold_narrative_md || "",
    );
    if (nextNarrative === null) return; // Cancel
    try {
      await api.patch(`/listings/${l.slug}`, {
        domain_logo_url: nextLogo.trim(),
        sold_narrative_md: nextNarrative,
      });
      toast.success(`${l.slug} · logo + narrative saved`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const startBulkAi = async () => {
    const missing = listings.filter(l => l.status === "active" && !l.ai_copy).length;
    if (!missing) {
      toast.info("Every active listing already has AI copy");
      return;
    }
    if (!window.confirm(`Generate AI copy for ${missing} listing(s)? This will run in the background.`)) return;
    try {
      await api.post("/listings/ai-copy/bulk?only_missing=true");
      setBulkAi(s => ({ ...s, status: "running", total: missing, done: 0, failed: 0 }));
      toast.success(`Bulk AI copy started for ${missing} listing(s)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start bulk AI");
    }
  };

  // Poll bulk AI status while running
  useEffect(() => {
    if (bulkAi.status !== "running") return;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get("/listings/ai-copy/bulk/status");
        setBulkAi(data);
        if (data.status === "done") {
          clearInterval(t);
          toast.success(`Bulk AI done · ${data.done} generated · ${data.failed} failed`);
          load();
        }
      } catch (e) { /* silent */ }
    }, 3000);
    return () => clearInterval(t);
  }, [bulkAi.status]);

  return (
    <div className="min-h-screen w-full flex bg-white" data-testid="listings-page">
      <Sidebar />
      <main className="flex-1 flex flex-col border-l border-[#eaecf0] min-w-0">
        <header className="h-14 border-b border-[#eaecf0] px-6 flex items-center gap-3">
          <span className="overline">{brand.brand_name}</span>
          <span className="text-[#D4D4D8]">/</span>
          <span className="text-sm font-medium">Marketplace</span>
          <div className="ml-auto">
            <button
              onClick={async () => {
                if (!confirm("Send every uncategorized listing to Claude for smart categorization? This may take a minute.")) return;
                setCategorizing(true);
                try {
                  const { data } = await api.post("/listings/bulk-categorize", { only_uncategorized: true, limit: 200 });
                  toast.success(`Categorized ${data.count} listing(s)${data.unclassified ? ` · ${data.unclassified} skipped` : ""}`);
                  await load();
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Categorization failed");
                } finally {
                  setCategorizing(false);
                }
              }}
              disabled={categorizing}
              data-testid="bulk-categorize-btn"
              className="text-xs px-3 py-1.5 rounded-sm border border-[#eaecf0] hover:bg-[#f4f4f5]"
              title="Send every uncategorized listing to Claude for one-shot classification"
            >
              {categorizing ? "Categorizing…" : "✨ Suggest categories with AI"}
            </button>
          </div>
        </header>

        <div className="border-b border-[#eaecf0] px-6 flex gap-6">
          {[
            { id: "listings", label: "Listings", count: listings.length },
            { id: "offers", label: "Offers", count: offers.length },
            { id: "categories", label: "Categories", count: LISTING_CATEGORIES.length },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`tab-${t.id}`}
              className={`py-3 text-sm border-b-2 -mb-px ${
                tab === t.id
                  ? "border-[#03142f] text-[#101828] font-medium"
                  : "border-transparent text-[#525252]"
              }`}
            >
              {t.label}
              <span className="ml-2 text-[10px] font-mono text-[#A1A1AA]">{t.count}</span>
            </button>
          ))}
        </div>

        {tab === "listings" && (
          <div className="border-b border-[#eaecf0] bg-[#FAFAFA] px-6 py-3 flex items-center justify-between gap-3">
            <div className="text-[11px] font-mono text-[#525252]">
              {listings.filter(l => l.status === "active" && !l.ai_copy).length} listing(s) without AI copy
            </div>
            <div className="flex items-center gap-3">
              {bulkAi.status === "running" && (
                <div className="text-[11px] font-mono text-[#03142f]" data-testid="bulk-ai-progress">
                  Generating · {bulkAi.done}/{bulkAi.total}
                  {bulkAi.current_domain ? ` · ${bulkAi.current_domain}` : ""}
                </div>
              )}
              <button
                onClick={startBulkAi}
                disabled={bulkAi.status === "running" || listingsLoading}
                className="px-3 py-1.5 border border-[#03142f] text-[#03142f] hover:bg-[#EEF2FF] text-xs font-mono flex items-center gap-1.5 disabled:opacity-40 rounded-sm"
                data-testid="bulk-ai-copy-btn"
              >
                <Sparkles className="w-3.5 h-3.5" strokeWidth={1.5} />
                Generate AI copy for all
              </button>
            </div>
          </div>
        )}

        {tab === "listings" ? (
          <div className="flex-1 overflow-auto relative">
            {selected.size > 0 && (
              <div
                data-testid="bulk-action-bar"
                className="sticky top-0 z-10 border-b border-[#03142f] bg-[#EEF2FF] px-6 py-3 flex flex-wrap items-center gap-3"
              >
                <span className="font-mono text-xs text-[#03142f]" data-testid="bulk-selected-count">
                  {selected.size} selected
                </span>
                <span className="text-[#A1A1AA]">·</span>
                <button
                  onClick={selectAllVisible}
                  data-testid="bulk-select-all"
                  className="text-[11px] font-mono uppercase text-[#03142f] hover:underline"
                >
                  Select all {listings.length}
                </button>
                <button
                  onClick={clearSelection}
                  data-testid="bulk-clear"
                  className="text-[11px] font-mono uppercase text-[#525252] hover:underline"
                >
                  Clear
                </button>
                <div className="flex-1" />
                <input
                  type="number"
                  value={bulkPrice}
                  onChange={(e) => setBulkPrice(e.target.value)}
                  placeholder="Set price on all"
                  data-testid="bulk-price-input"
                  className="w-40 text-sm font-mono px-2 py-1 border border-[#eaecf0] rounded-sm outline-none"
                />
                <button
                  onClick={bulkSetPrice}
                  disabled={bulkBusy}
                  data-testid="bulk-set-price-btn"
                  className="text-[11px] font-mono uppercase px-3 py-1.5 border border-[#03142f] text-[#03142f] hover:bg-[#03142f] hover:text-white"
                >
                  <DollarSign className="w-3 h-3 inline mr-1" strokeWidth={2} /> Set price
                </button>
                <button
                  onClick={() => doBulk("clear_price")}
                  disabled={bulkBusy}
                  data-testid="bulk-clear-price-btn"
                  className="text-[11px] font-mono uppercase px-3 py-1.5 border border-[#B45309] text-[#B45309] hover:bg-[#B45309] hover:text-white"
                >
                  Offer-only
                </button>
                <button
                  onClick={() => doBulk("archive")}
                  disabled={bulkBusy}
                  data-testid="bulk-archive-btn"
                  className="text-[11px] font-mono uppercase px-3 py-1.5 border border-[#525252] text-[#525252] hover:bg-[#525252] hover:text-white"
                >
                  <Archive className="w-3 h-3 inline mr-1" strokeWidth={2} /> Archive
                </button>
                <button
                  onClick={() => doBulk("activate")}
                  disabled={bulkBusy}
                  data-testid="bulk-activate-btn"
                  className="text-[11px] font-mono uppercase px-3 py-1.5 border border-[#047857] text-[#047857] hover:bg-[#047857] hover:text-white"
                >
                  <RotateCcw className="w-3 h-3 inline mr-1" strokeWidth={2} /> Activate
                </button>
                <button
                  onClick={() => doBulk("delete")}
                  disabled={bulkBusy}
                  data-testid="bulk-delete-btn"
                  className="text-[11px] font-mono uppercase px-3 py-1.5 border border-[#BE123C] text-[#BE123C] hover:bg-[#BE123C] hover:text-white"
                >
                  <Trash2 className="w-3 h-3 inline mr-1" strokeWidth={2} /> Delete
                </button>
              </div>
            )}
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#eaecf0] bg-[#FAFAFA]">
                  <th className="text-left pl-6 pr-2 py-2.5 w-8">
                    <input
                      type="checkbox"
                      data-testid="select-all-header"
                      checked={selected.size > 0 && selected.size === listings.length}
                      onChange={selected.size === listings.length ? clearSelection : selectAllVisible}
                      className="cursor-pointer"
                    />
                  </th>
                  <th className="text-left px-4 py-2.5 overline">Domain</th>
                  <th className="text-left px-4 py-2.5 overline">Price</th>
                  <th className="text-left px-4 py-2.5 overline">Status</th>
                  <th className="text-left px-4 py-2.5 overline">Views</th>
                  <th className="text-left px-4 py-2.5 overline">Uniques</th>
                  <th className="text-left px-4 py-2.5 overline">Offers</th>
                  <th className="text-left px-4 py-2.5 overline">Forward</th>
                  <th className="text-left px-4 py-2.5 overline">Template</th>
                  <th className="text-left px-4 py-2.5 overline">Category</th>
                  <th className="text-left px-4 py-2.5 overline">Premium</th>
                  <th className="text-left px-4 py-2.5 overline">Brand-ready</th>
                  <th className="text-right px-6 py-2.5 overline">Actions</th>
                </tr>
              </thead>
              <tbody>
                {listings.length === 0 && (
                  <tr>
                    <td colSpan={9} className="px-6 py-16 text-center">
                      <div className="overline mb-2">No listings yet</div>
                      <div className="text-sm text-[#525252]">
                        Enable &quot;List for sale on success&quot; on a snipe to auto-create a lander.
                      </div>
                    </td>
                  </tr>
                )}
                {listings.map((l, idx) => (
                  <tr
                    key={l.slug}
                    className={`border-b border-[#eaecf0] hover:bg-[#FAFAFA] ${
                      selected.has(l.slug) ? "bg-[#EEF2FF]" : ""
                    }`}
                    data-testid={`listing-row-${l.slug}`}
                  >
                    <td className="pl-6 pr-2 py-3 w-8">
                      <input
                        type="checkbox"
                        data-testid={`row-checkbox-${l.slug}`}
                        checked={selected.has(l.slug)}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => toggleRow(l.slug, idx, e.nativeEvent)}
                        className="cursor-pointer"
                      />
                    </td>
                    <td className="px-4 py-3 font-mono">{l.domain}</td>
                    <td className="px-4 py-3">
                      {editingSlug === l.slug ? (
                        <input
                          autoFocus
                          data-testid={`price-input-${l.slug}`}
                          value={priceDraft}
                          onChange={(e) => setPriceDraft(e.target.value)}
                          onBlur={() => savePrice(l.slug, priceDraft)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") savePrice(l.slug, priceDraft);
                            if (e.key === "Escape") setEditingSlug(null);
                          }}
                          placeholder="blank = offer only"
                          className="w-32 text-sm font-mono px-2 py-1 border border-[#03142f] rounded-sm outline-none"
                        />
                      ) : (
                        <button
                          onClick={() => {
                            setEditingSlug(l.slug);
                            setPriceDraft(l.price ? String(l.price) : "");
                          }}
                          data-testid={`price-cell-${l.slug}`}
                          className="text-left font-mono tabular-nums hover:bg-[#F4F4F5] px-2 py-1 -mx-2 -my-1 rounded-sm"
                          title="Click to edit price. Leave blank to force offer-only."
                        >
                          {l.price ? (
                            <span>{(l.currency || "EUR")} {Number(l.price).toLocaleString()}</span>
                          ) : (
                            <span className="text-[#B45309]" data-testid={`price-empty-${l.slug}`}>
                              — set price
                            </span>
                          )}
                        </button>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <select
                        value={l.status}
                        onChange={(e) => setStatus(l.slug, e.target.value)}
                        data-testid={`status-select-${l.slug}`}
                        className="text-[10px] font-mono px-2 py-0.5 border border-[#eaecf0] rounded-sm bg-white"
                      >
                        <option value="active">active</option>
                        <option value="sold">sold</option>
                        <option value="inactive">inactive</option>
                      </select>
                    </td>
                    <td className="px-4 py-3 font-mono tabular-nums">{l.views || 0}</td>
                    <td className="px-4 py-3 font-mono tabular-nums">{l.unique_visitors || 0}</td>
                    <td className="px-4 py-3 font-mono tabular-nums">{l.offer_count || 0}</td>
                    <td className="px-4 py-3">
                      {l.forward_status === "active" && (
                        <span className="text-[10px] font-mono px-2 py-0.5 bg-[#ECFDF5] text-[#047857] rounded-sm">LIVE</span>
                      )}
                      {l.forward_status === "failed" && (
                        <span
                          className="text-[10px] font-mono px-2 py-0.5 bg-[#FFF1F2] text-[#BE123C] rounded-sm"
                          title={l.forward_message || ""}
                        >FAIL</span>
                      )}
                      {l.forward_status === "unsupported" && (
                        <span
                          className="text-[10px] font-mono px-2 py-0.5 bg-[#EFF6FF] text-[#2563EB] rounded-sm"
                          title={l.forward_message || ""}
                        >SPACESHIP</span>
                      )}
                      {(!l.forward_status || l.forward_status === "pending") && (
                        <span className="text-[10px] font-mono px-2 py-0.5 bg-[#F4F4F5] text-[#525252] rounded-sm">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <select
                        value={l.template || "editorial"}
                        onChange={async (e) => {
                          const v = e.target.value;
                          try {
                            await api.patch(`/listings/${l.slug}`, { template: v });
                            setListings(prev => prev.map(x => x.slug === l.slug ? { ...x, template: v } : x));
                            toast.success(`${l.slug} → ${v}`);
                          } catch (err) {
                            toast.error("Failed to save template");
                          }
                        }}
                        data-testid={`template-select-${l.slug}`}
                        className="text-[10px] font-mono px-2 py-0.5 border border-[#eaecf0] rounded-sm bg-white"
                      >
                        <option value="editorial">editorial</option>
                        <option value="minimal">minimal</option>
                        <option value="poster">poster</option>
                      </select>
                    </td>
                    <td className="px-4 py-3">
                      <select
                        value={l.category || ""}
                        onChange={async (e) => {
                          const v = e.target.value;
                          try {
                            await api.patch(`/listings/${l.slug}`, { category: v || null });
                            setListings(prev => prev.map(x => x.slug === l.slug ? { ...x, category: v } : x));
                          } catch { toast.error("Failed to save category"); }
                        }}
                        data-testid={`category-select-${l.slug}`}
                        className="text-[10px] font-mono px-2 py-0.5 border border-[#eaecf0] rounded-sm bg-white"
                      >
                        <option value="">—</option>
                        {LISTING_CATEGORIES.map((c) => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-start gap-2">
                        <input
                          type="checkbox"
                          checked={!!l.is_premium}
                          onChange={async (e) => {
                            const v = e.target.checked;
                            try {
                              await api.patch(`/listings/${l.slug}`, { is_premium: v });
                              setListings(prev => prev.map(x => x.slug === l.slug ? { ...x, is_premium: v } : x));
                            } catch { toast.error("Failed to save"); }
                          }}
                          data-testid={`premium-toggle-${l.slug}`}
                          className="w-4 h-4 mt-1"
                          title="Feature this listing in the Premium section"
                        />
                        {l.is_premium && (
                          <PremiumStoryField
                            listing={l}
                            onSave={(story) => setListings(prev => prev.map(x => x.slug === l.slug ? { ...x, premium_story: story } : x))}
                          />
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={!!l.is_brand_ready}
                          onChange={async (e) => {
                            const v = e.target.checked;
                            try {
                              await api.patch(`/listings/${l.slug}`, { is_brand_ready: v });
                              setListings(prev => prev.map(x => x.slug === l.slug ? { ...x, is_brand_ready: v } : x));
                            } catch { toast.error("Failed to save"); }
                          }}
                          data-testid={`brand-ready-toggle-${l.slug}`}
                          className="w-4 h-4"
                          title="Feature on /brands — the launch-ready brand-identity showcase"
                        />
                        {l.is_brand_ready && (
                          <ListingLogoUpload
                            listing={l}
                            onSaved={(url) => setListings(prev => prev.map(x => x.slug === l.slug ? { ...x, domain_logo_url: url } : x))}
                          />
                        )}
                        {l.is_brand_ready && (
                          <BrandStarterField
                            listing={l}
                            onSaved={(patch) => setListings(prev => prev.map(x => x.slug === l.slug ? { ...x, ...patch } : x))}
                          />
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-3 text-right">
                      <div className="flex items-center gap-1 justify-end">
                        <button
                          onClick={() => runForward(l.slug)}
                          className="p-1 hover:bg-[#F4F4F5] rounded-sm"
                          data-testid={`forward-${l.slug}`}
                          title="Set up URL forwarding at registrar"
                        >
                          <Zap className="w-3.5 h-3.5" strokeWidth={1.5} />
                        </button>
                        <button
                          onClick={() => runAiCopy(l.slug)}
                          disabled={aiBusy === l.slug}
                          className="p-1 hover:bg-[#F4F4F5] rounded-sm disabled:opacity-40"
                          data-testid={`ai-copy-${l.slug}`}
                          title="Generate AI marketing copy (GPT 5.6 Sol)"
                        >
                          <Sparkles
                            className={`w-3.5 h-3.5 ${aiBusy === l.slug ? "animate-pulse text-[#03142f]" : (l.ai_copy ? "text-[#03142f]" : "")}`}
                            strokeWidth={1.5}
                          />
                        </button>
                        <button onClick={() => copy(l.slug)} className="p-1 hover:bg-[#F4F4F5] rounded-sm" data-testid={`copy-${l.slug}`}>
                          <Copy className="w-3.5 h-3.5" strokeWidth={1.5} />
                        </button>
                        <button
                          onClick={() => editExtras(l)}
                          className="p-1 hover:bg-[#F4F4F5] rounded-sm"
                          data-testid={`extras-${l.slug}`}
                          title="Edit domain logo URL + sold narrative"
                        >
                          <ImageIcon
                            className={`w-3.5 h-3.5 ${l.domain_logo_url || l.sold_narrative_md ? "text-[#03142f]" : ""}`}
                            strokeWidth={1.5}
                          />
                        </button>
                        <a href={`/l/${l.slug}`} target="_blank" rel="noreferrer" className="p-1 hover:bg-[#F4F4F5] rounded-sm" data-testid={`open-${l.slug}`}>
                          <ExternalLink className="w-3.5 h-3.5" strokeWidth={1.5} />
                        </a>
                        <button onClick={() => remove(l.slug)} className="p-1 hover:bg-[#F4F4F5] rounded-sm text-[#BE123C]" data-testid={`remove-${l.slug}`}>
                          <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : tab === "offers" ? (
          <div className="flex-1 overflow-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#eaecf0] bg-[#FAFAFA]">
                  <th className="text-left px-6 py-2.5 overline">When</th>
                  <th className="text-left px-4 py-2.5 overline">Domain</th>
                  <th className="text-left px-4 py-2.5 overline">Intent</th>
                  <th className="text-left px-4 py-2.5 overline">Amount</th>
                  <th className="text-left px-4 py-2.5 overline">From</th>
                  <th className="text-left px-4 py-2.5 overline">Message</th>
                </tr>
              </thead>
              <tbody>
                {offers.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-6 py-16 text-center text-sm text-[#525252]">
                      No offers received yet.
                    </td>
                  </tr>
                )}
                {offers.map((o) => (
                  <tr key={o.id} className="border-b border-[#eaecf0] hover:bg-[#FAFAFA]">
                    <td className="px-6 py-3 font-mono text-xs text-[#525252]">
                      {new Date(o.created_at).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 font-mono">
                      <Link to={`/l/${o.slug}`} className="hover:underline">{o.domain}</Link>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className="text-[10px] font-mono px-2 py-0.5 rounded-sm"
                        style={{
                          background: o.intent === "buy" ? "#ECFDF5" : "#EFF6FF",
                          color: o.intent === "buy" ? "#047857" : "#2563EB",
                        }}
                      >
                        {o.intent.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-mono tabular-nums">
                      {o.currency} {Number(o.amount).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-xs">
                      <div>{o.name}</div>
                      <div className="text-[#525252] font-mono">{o.email}</div>
                    </td>
                    <td className="px-4 py-3 text-xs text-[#525252] max-w-xs truncate">
                      {o.message || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="flex-1 overflow-auto p-6" data-testid="categories-panel">
            <div className="max-w-5xl">
              <div className="mb-6">
                <h2 className="text-xl font-medium mb-1">Category hero images</h2>
                <p className="text-xs text-[#525252]">
                  Editorial banner shown above the H1 on <code className="font-mono">/category/:slug</code> pages.
                  Landscape photos, JPG/PNG/WEBP, up to 8 MB. Leave empty to fall back to a plain header.
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {LISTING_CATEGORIES.map((cat) => {
                  const entry = categoryHeroes[cat] || { hero_url: "", focal_x: 50, focal_y: 50 };
                  return (
                    <CategoryHeroTile
                      key={cat}
                      category={cat}
                      heroUrl={entry.hero_url}
                      focalX={entry.focal_x}
                      focalY={entry.focal_y}
                      onChange={(next) =>
                        setCategoryHeroes((m) => ({
                          ...m,
                          [cat]: {
                            hero_url: next.hero_url ?? "",
                            focal_x: typeof next.focal_x === "number" ? next.focal_x : 50,
                            focal_y: typeof next.focal_y === "number" ? next.focal_y : 50,
                          },
                        }))
                      }
                    />
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </main>

      {aiPreview && (
        <div
          className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-6"
          onClick={() => setAiPreview(null)}
          data-testid="ai-preview-modal"
        >
          <div
            className="bg-white max-w-lg w-full rounded-sm border border-[#eaecf0] p-8"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-6">
              <div>
                <div className="overline text-[#525252]">AI copy · GPT 5.6 Sol</div>
                <div className="font-mono text-sm mt-1">{aiPreview.slug}</div>
              </div>
              <button
                onClick={() => setAiPreview(null)}
                className="p-1 hover:bg-[#F4F4F5] rounded-sm"
                data-testid="ai-preview-close"
              >
                <X className="w-4 h-4" strokeWidth={1.5} />
              </button>
            </div>
            <div className="space-y-4 text-sm">
              <div>
                <div className="overline text-[10px] text-[#525252] mb-1">Headline</div>
                <div className="font-medium text-lg leading-tight" data-testid="ai-headline">
                  {aiPreview.headline || "—"}
                </div>
              </div>
              {aiPreview.tagline && (
                <div>
                  <div className="overline text-[10px] text-[#525252] mb-1">Tagline</div>
                  <div className="italic text-[#525252]" data-testid="ai-tagline">{aiPreview.tagline}</div>
                </div>
              )}
              <div>
                <div className="overline text-[10px] text-[#525252] mb-1">Description</div>
                <div className="leading-relaxed" data-testid="ai-description">{aiPreview.description || "—"}</div>
              </div>
              {aiPreview.audience && (
                <div>
                  <div className="overline text-[10px] text-[#525252] mb-1">Audience</div>
                  <div className="text-[#525252]" data-testid="ai-audience">{aiPreview.audience}</div>
                </div>
              )}
            </div>
            <div className="mt-6 flex gap-2 justify-end">
              <button
                onClick={() => runAiCopy(aiPreview.slug)}
                disabled={aiBusy === aiPreview.slug}
                className="px-3 py-2 border border-[#eaecf0] text-xs font-mono hover:bg-[#F4F4F5] disabled:opacity-40"
                data-testid="ai-regenerate"
              >
                {aiBusy === aiPreview.slug ? "Generating..." : "Regenerate"}
              </button>
              <a
                href={`/l/${aiPreview.slug}`}
                target="_blank"
                rel="noreferrer"
                className="px-3 py-2 bg-[#03142f] text-white text-xs font-mono hover:bg-[#001A6B]"
                data-testid="ai-preview-lander"
              >
                View lander →
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


/**
 * Compact popover for editing a listing's "premium_story" copy — the
 * short curator's note rendered only on `/premium`. Renders as a tiny
 * ✎-icon button next to the Premium checkbox; clicking opens a popover
 * with a textarea + save/cancel. Persists via PATCH /listings/{slug}.
 */
function PremiumStoryField({ listing, onSave }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(listing.premium_story || "");
  const [saving, setSaving] = useState(false);
  const hasStory = !!listing.premium_story;

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/listings/${listing.slug}`, { premium_story: text.trim() || null });
      onSave?.(text.trim());
      setOpen(false);
      toast.success("Story saved");
    } catch {
      toast.error("Failed to save story");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="px-1.5 py-0.5 rounded-sm text-[10px] font-mono uppercase tracking-wider"
        style={{
          background: hasStory ? "#fef3c7" : "#f4f4f5",
          color: hasStory ? "#92400e" : "#525252",
          border: "1px solid " + (hasStory ? "#fbbf24" : "#eaecf0"),
        }}
        data-testid={`premium-story-toggle-${listing.slug}`}
        title={hasStory ? "Edit the curator's note shown on /premium" : "Add a curator's note (shown only on /premium)"}
      >
        {hasStory ? "✎ Story" : "+ Story"}
      </button>
      {open && (
        <div
          className="absolute z-30 top-full mt-1 right-0 w-80 bg-white border border-[#eaecf0] rounded-lg shadow-lg p-3"
          data-testid={`premium-story-popover-${listing.slug}`}
        >
          <div className="overline mb-2">Why this domain matters</div>
          <p className="text-[10px] text-[#98a2b3] mb-2">
            Short curator's note (1–2 sentences). Shown only on the <strong>/premium</strong> page, italicized under the tagline.
          </p>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="e.g. Two-syllable, exact-match keyword, .com — the flag every AI infra founder wants planted."
            rows={4}
            className="w-full text-sm p-2 border border-[#eaecf0] rounded-sm font-serif"
            style={{ resize: "vertical" }}
            data-testid={`premium-story-textarea-${listing.slug}`}
            maxLength={280}
          />
          <div className="text-[10px] text-[#98a2b3] mt-1 mb-2 text-right font-mono">
            {text.length} / 280
          </div>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => { setOpen(false); setText(listing.premium_story || ""); }}
              className="text-[11px] px-2 py-1 rounded hover:bg-[#f4f4f5]"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={save}
              disabled={saving}
              className="text-[11px] px-2 py-1 rounded"
              style={{ background: "#03142f", color: "white" }}
              data-testid={`premium-story-save-${listing.slug}`}
            >
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}



/**
 * Drag-and-drop logo upload for a single listing. Renders as a small
 * thumbnail (or a "+ Logo" chip when empty). Drop or click to upload;
 * on success, PATCHes `domain_logo_url` on the listing so /brands
 * immediately shows the real logo instead of the dark fallback slab.
 */
function ListingLogoUpload({ listing, onSaved }) {
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const logoUrl = listing.domain_logo_url || "";

  const backend = process.env.REACT_APP_BACKEND_URL || "";
  const fullSrc = logoUrl.startsWith("/api/")
    ? `${backend}${logoUrl}`
    : logoUrl;

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("Max logo size is 2 MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const up = await api.post("/upload/listing-logo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = up.data?.url;
      if (!url) throw new Error("no url");
      await api.patch(`/listings/${listing.slug}`, { domain_logo_url: url });
      onSaved?.(url);
      toast.success("Logo saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const removeLogo = async () => {
    try {
      await api.patch(`/listings/${listing.slug}`, { domain_logo_url: null });
      onSaved?.("");
      toast.success("Logo removed");
    } catch { toast.error("Failed to remove"); }
  };

  return (
    <label
      onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => { e.preventDefault(); setDragging(false); upload(e.dataTransfer.files?.[0]); }}
      style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        padding: logoUrl ? 2 : "3px 8px", cursor: "pointer",
        borderRadius: 4,
        border: dragging
          ? "1px dashed var(--cd-blue-500, #2563eb)"
          : logoUrl ? "1px solid transparent" : "1px dashed var(--cd-gray-200)",
        background: dragging ? "#eff6ff" : "transparent",
        transition: "background 120ms, border-color 120ms",
      }}
      title="Drag a PNG/SVG here or click to upload"
      data-testid={`listing-logo-upload-${listing.slug}`}
    >
      {logoUrl ? (
        <>
          <img
            src={fullSrc}
            alt=""
            style={{ width: 22, height: 22, objectFit: "contain", background: "white", borderRadius: 3, border: "1px solid var(--cd-gray-200)" }}
          />
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); removeLogo(); }}
            style={{ fontSize: 10, color: "#dc2626", padding: 0, background: "transparent", border: "none" }}
            data-testid={`listing-logo-remove-${listing.slug}`}
            aria-label="Remove logo"
          >
            ×
          </button>
        </>
      ) : (
        <span style={{ fontSize: 10, fontFamily: "ui-monospace, monospace", textTransform: "uppercase", letterSpacing: ".05em", color: "var(--cd-gray-500)" }}>
          {uploading ? "Uploading…" : "+ Logo"}
        </span>
      )}
      <input
        type="file"
        accept="image/png,image/jpeg,image/webp,image/svg+xml"
        onChange={(e) => upload(e.target.files?.[0])}
        style={{ display: "none" }}
        data-testid={`listing-logo-input-${listing.slug}`}
      />
    </label>
  );
}



/**
 * "Brand starter" bundle editor. Toggles `is_brand_starter` + captures a
 * newline-separated list of what's included in the package (rendered as
 * bullets on the lander). One-price bundle: no separate line items.
 */
function BrandStarterField({ listing, onSaved }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState((listing.brand_starter_includes || []).join("\n"));
  const [saving, setSaving] = useState(false);
  const isOn = !!listing.is_brand_starter;

  const save = async (toggle) => {
    setSaving(true);
    const nextOn = toggle === undefined ? isOn : toggle;
    const includes = text.split("\n").map((l) => l.trim()).filter(Boolean).slice(0, 8);
    try {
      await api.patch(`/listings/${listing.slug}`, {
        is_brand_starter: nextOn,
        brand_starter_includes: nextOn ? includes : null,
      });
      onSaved?.({ is_brand_starter: nextOn, brand_starter_includes: nextOn ? includes : null });
      if (toggle !== undefined) toast.success(nextOn ? "Brand starter enabled" : "Brand starter off");
      else { toast.success("Bundle contents saved"); setOpen(false); }
    } catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => (isOn ? setOpen((v) => !v) : save(true))}
        className="px-1.5 py-0.5 rounded-sm text-[10px] font-mono uppercase tracking-wider"
        style={{
          background: isOn ? "#dbeafe" : "#f4f4f5",
          color: isOn ? "#1e40af" : "#525252",
          border: "1px solid " + (isOn ? "#93c5fd" : "#eaecf0"),
        }}
        data-testid={`starter-toggle-${listing.slug}`}
        title={isOn ? "Edit bundle contents (or click to keep off)" : "Bundle this domain + logo + lander as a Brand Starter"}
      >
        {isOn ? "★ Starter" : "+ Starter"}
      </button>
      {open && isOn && (
        <div
          className="absolute z-30 top-full mt-1 right-0 w-80 bg-white border border-[#eaecf0] rounded-lg shadow-lg p-3"
          data-testid={`starter-popover-${listing.slug}`}
        >
          <div className="overline mb-2">Brand starter · what's included</div>
          <p className="text-[10px] text-[#98a2b3] mb-2">
            One line per bullet. Rendered on the lander under "Brand starter bundle".
          </p>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={"Domain transferred within 24h\nLogo files (SVG + PNG)\nLander template ready to publish\nOne round of typography tweaks"}
            rows={5}
            className="w-full text-sm p-2 border border-[#eaecf0] rounded-sm"
            style={{ resize: "vertical" }}
            data-testid={`starter-textarea-${listing.slug}`}
          />
          <div className="flex justify-between mt-2">
            <button
              type="button"
              onClick={() => save(false)}
              className="text-[11px] text-[#dc2626] px-2 py-1"
              data-testid={`starter-disable-${listing.slug}`}
            >
              Turn off
            </button>
            <div className="flex gap-2">
              <button type="button" onClick={() => setOpen(false)} className="text-[11px] px-2 py-1 rounded hover:bg-[#f4f4f5]">Cancel</button>
              <button
                type="button"
                onClick={() => save()}
                disabled={saving}
                className="text-[11px] px-2 py-1 rounded text-white"
                style={{ background: "#03142f" }}
                data-testid={`starter-save-${listing.slug}`}
              >
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}



/**
 * Single category row in the Categories admin tab.
 *  · Empty state: drag/drop or click to upload a landscape hero image.
 *  · With image: click anywhere on the preview to nudge the focal point;
 *    the crosshair marker + persisted `focal_x` / `focal_y` (0-100 %)
 *    are what the public `/category/:slug` band uses for
 *    `background-position` so wide banners never chop the subject on
 *    mobile. All persistence goes through `PUT /listings/categories/hero`.
 */
function CategoryHeroTile({ category, heroUrl, focalX = 50, focalY = 50, onChange }) {
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [saving, setSaving] = useState(false);
  // Unsplash picker state — kept local so each tile owns its own popover.
  const [picker, setPicker] = useState({ open: false, loading: false, photos: [], picking: null });
  const backend = process.env.REACT_APP_BACKEND_URL || "";
  const fullSrc = heroUrl?.startsWith("/api/") ? `${backend}${heroUrl}` : heroUrl;

  const persist = async (payload) => {
    // payload: { hero_url, focal_x, focal_y } — nulls clear.
    const { data } = await api.put("/listings/categories/hero", {
      category,
      hero_url: payload.hero_url ?? null,
      focal_x: payload.focal_x ?? null,
      focal_y: payload.focal_y ?? null,
    });
    onChange?.({
      hero_url: data?.hero_url || "",
      focal_x: typeof data?.focal_x === "number" ? data.focal_x : 50,
      focal_y: typeof data?.focal_y === "number" ? data.focal_y : 50,
    });
  };

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error("Max hero size is 8 MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const up = await api.post("/upload/category-hero", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = up.data?.url;
      if (!url) throw new Error("no url");
      // Reset focal to centre on every new upload — the old one likely
      // pointed at a different subject.
      await persist({ hero_url: url, focal_x: 50, focal_y: 50 });
      toast.success(`${category} · hero saved`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const clear = async () => {
    if (!heroUrl) return;
    if (!window.confirm(`Remove hero image for ${category}?`)) return;
    try {
      await persist({ hero_url: null });
      toast.warning(`${category} · hero cleared`);
    } catch {
      toast.error("Failed to clear");
    }
  };

  // Click on the preview when a hero is set → move the focal marker to
  // that position and save. Prevented while the file picker is active.
  const setFocal = async (e) => {
    if (!heroUrl || saving) return;
    e.preventDefault();
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    const fx = Math.round(((e.clientX - rect.left) / rect.width) * 100);
    const fy = Math.round(((e.clientY - rect.top) / rect.height) * 100);
    setSaving(true);
    try {
      await persist({ hero_url: heroUrl, focal_x: fx, focal_y: fy });
      toast.success(`${category} · focal ${fx}%,${fy}%`);
    } catch {
      toast.error("Failed to save focal");
    } finally {
      setSaving(false);
    }
  };

  const openPicker = async () => {
    setPicker({ open: true, loading: true, photos: [], picking: null });
    try {
      const { data } = await api.get("/listings/categories/hero/unsplash", { params: { category } });
      setPicker({ open: true, loading: false, photos: data?.photos || [], picking: null });
    } catch (e) {
      setPicker({ open: false, loading: false, photos: [], picking: null });
      const msg = e?.response?.status === 429
        ? "Unsplash hourly limit reached — try again in a bit"
        : (e?.response?.data?.detail || "Unsplash search failed");
      toast.error(msg);
    }
  };

  const pickPhoto = async (p) => {
    setPicker((s) => ({ ...s, picking: p.id }));
    try {
      const { data } = await api.post("/listings/categories/hero/from-unsplash", {
        category,
        photo_id: p.id,
        full_url: p.full,
        download_location: p.download_location || null,
        photographer: p.photographer || "",
        photographer_url: p.photographer_url || "",
      });
      onChange?.({
        hero_url: data?.hero_url || "",
        focal_x: typeof data?.focal_x === "number" ? data.focal_x : 50,
        focal_y: typeof data?.focal_y === "number" ? data.focal_y : 50,
      });
      setPicker({ open: false, loading: false, photos: [], picking: null });
      toast.success(`${category} · ${p.photographer ? `photo by ${p.photographer}` : "hero saved"}`);
    } catch (e) {
      setPicker((s) => ({ ...s, picking: null }));
      toast.error(e?.response?.data?.detail || "Failed to save photo");
    }
  };

  return (
    <div
      className="border border-[#eaecf0] rounded-sm overflow-hidden bg-white flex flex-col"
      data-testid={`category-hero-tile-${category.toLowerCase().replace(/\s+/g, "-")}`}
    >
      {heroUrl ? (
        <div
          onClick={setFocal}
          data-testid={`category-hero-focal-${category.toLowerCase().replace(/\s+/g, "-")}`}
          style={{
            position: "relative",
            aspectRatio: "16 / 7",
            backgroundImage: `url(${fullSrc})`,
            backgroundSize: "cover",
            backgroundPosition: `${focalX}% ${focalY}%`,
            borderBottom: "1px solid #eaecf0",
            cursor: saving ? "wait" : "crosshair",
          }}
          title="Click to set the crop focal point"
        >
          {/* Focal marker */}
          <div
            aria-hidden
            style={{
              position: "absolute",
              left: `${focalX}%`,
              top: `${focalY}%`,
              transform: "translate(-50%, -50%)",
              width: 22,
              height: 22,
              borderRadius: "50%",
              border: "2px solid white",
              boxShadow: "0 0 0 1px rgba(0,0,0,.45), 0 2px 6px rgba(0,0,0,.35)",
              pointerEvents: "none",
            }}
          />
          <div
            aria-hidden
            style={{
              position: "absolute",
              left: 8, bottom: 8,
              fontSize: 10, fontFamily: "ui-monospace, monospace",
              color: "white",
              background: "rgba(0,0,0,.55)",
              padding: "2px 6px",
              borderRadius: 3,
              letterSpacing: ".05em",
              pointerEvents: "none",
            }}
          >
            {saving ? "SAVING…" : `FOCAL ${focalX}%,${focalY}%`}
          </div>
        </div>
      ) : (
        <label
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => { e.preventDefault(); setDragging(false); upload(e.dataTransfer.files?.[0]); }}
          style={{
            display: "block",
            position: "relative",
            aspectRatio: "16 / 7",
            background: "#fafafa",
            borderBottom: "1px solid #eaecf0",
            cursor: "pointer",
            outline: dragging ? "2px dashed #2563eb" : "none",
            outlineOffset: -6,
          }}
          title="Drag a JPG/PNG/WEBP here or click to upload"
        >
          <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 8, color: "#a1a1aa", fontSize: 11, fontFamily: "ui-monospace, monospace", letterSpacing: ".08em", textTransform: "uppercase" }}>
            <span>{uploading ? "Uploading…" : "Drop or click to upload"}</span>
            <button
              type="button"
              onClick={(ev) => { ev.preventDefault(); ev.stopPropagation(); openPicker(); }}
              className="text-[10px] px-2.5 py-1 rounded-full bg-white border border-[#eaecf0] hover:border-[#03142f] hover:text-[#03142f] text-[#525252] normal-case tracking-normal"
              style={{ fontFamily: "inherit" }}
              data-testid={`category-hero-unsplash-btn-${category.toLowerCase().replace(/\s+/g, "-")}`}
            >
              ✨ Suggest from Unsplash
            </button>
          </div>
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            onChange={(e) => upload(e.target.files?.[0])}
            style={{ display: "none" }}
            data-testid={`category-hero-input-${category.toLowerCase().replace(/\s+/g, "-")}`}
          />
        </label>
      )}
      <div className="flex items-center justify-between px-4 py-2.5 gap-2">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium truncate" title={category}>{category}</div>
          <div
            className="text-[10px] font-mono text-[#a1a1aa] mt-0.5 truncate"
            title={heroUrl ? "Click preview to nudge focal" : "No hero image"}
          >
            {heroUrl ? "Focal draggable" : "No hero image"}
          </div>
        </div>
        {heroUrl ? (
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={openPicker}
              className="text-[11px] px-2 py-1 rounded-sm text-[#03142f] hover:bg-[#f4f4f5]"
              data-testid={`category-hero-unsplash-swap-${category.toLowerCase().replace(/\s+/g, "-")}`}
              title="Swap for an Unsplash pick"
            >
              ✨
            </button>
            <label
              className="text-[11px] px-2 py-1 rounded-sm text-[#03142f] hover:bg-[#f4f4f5] cursor-pointer"
              data-testid={`category-hero-replace-${category.toLowerCase().replace(/\s+/g, "-")}`}
            >
              Replace
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                onChange={(e) => upload(e.target.files?.[0])}
                style={{ display: "none" }}
              />
            </label>
            <button
              type="button"
              onClick={clear}
              className="text-[11px] px-2 py-1 rounded-sm text-[#be123c] hover:bg-[#fff1f2]"
              data-testid={`category-hero-clear-${category.toLowerCase().replace(/\s+/g, "-")}`}
            >
              Remove
            </button>
          </div>
        ) : null}
      </div>
      {picker.open && (
        <div
          className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-6"
          onClick={() => setPicker({ open: false, loading: false, photos: [], picking: null })}
          data-testid={`category-hero-unsplash-modal-${category.toLowerCase().replace(/\s+/g, "-")}`}
        >
          <div
            className="bg-white max-w-3xl w-full rounded-sm border border-[#eaecf0] p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-5">
              <div>
                <div className="overline text-[#525252]">Unsplash · {category}</div>
                <div className="text-sm mt-1">Pick a photo — it will be downloaded, uploaded to your storage, and set as the hero.</div>
              </div>
              <button
                onClick={() => setPicker({ open: false, loading: false, photos: [], picking: null })}
                className="p-1 hover:bg-[#F4F4F5] rounded-sm"
                data-testid={`category-hero-unsplash-close-${category.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <X className="w-4 h-4" strokeWidth={1.5} />
              </button>
            </div>
            {picker.loading ? (
              <div className="py-16 text-center text-sm text-[#525252]">Searching Unsplash…</div>
            ) : picker.photos.length === 0 ? (
              <div className="py-16 text-center text-sm text-[#525252]">No results — try uploading a file instead.</div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {picker.photos.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => pickPhoto(p)}
                    disabled={!!picker.picking}
                    className="group relative overflow-hidden rounded-sm border border-[#eaecf0] hover:border-[#03142f] transition-colors disabled:opacity-60 text-left"
                    data-testid={`category-hero-unsplash-photo-${p.id}`}
                    style={{ aspectRatio: "16 / 9", padding: 0, background: "#f4f4f5" }}
                  >
                    <img src={p.thumb} alt={p.alt || ""} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} loading="lazy" />
                    <div
                      className="absolute inset-x-0 bottom-0 text-white text-[10px] px-2 py-1.5 flex items-center justify-between"
                      style={{ background: "linear-gradient(to top, rgba(0,0,0,.75), rgba(0,0,0,0))" }}
                    >
                      <span className="truncate font-mono">
                        {picker.picking === p.id ? "Saving…" : (p.photographer ? `© ${p.photographer}` : "Unsplash")}
                      </span>
                      {picker.picking === p.id ? null : (
                        <span className="opacity-0 group-hover:opacity-100 transition-opacity">Pick →</span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
            <div className="text-[10px] font-mono text-[#a1a1aa] mt-4">
              Photos via <a href="https://unsplash.com" target="_blank" rel="noreferrer" className="underline">Unsplash</a>. Each pick fires the required download-tracking ping and stores photographer attribution.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
